export interface FieldError {
  field: string;
  message: string;
}

export interface ParsedApiError {
  generalError?: string;
  fieldErrors: Record<string, string>;
  isValidationError: boolean;
  rawErrorData?: unknown; // Preserve raw backend error data
}

/**
 * Parse API error response and extract field-specific validation errors
 */
export const parseApiError = async (response: Response): Promise<ParsedApiError> => {
  const result: ParsedApiError = {
    fieldErrors: {},
    isValidationError: false,
  };

  try {
    const errorData = await response.json();
    const errorMessage = errorData?.error?.message || errorData?.message || response.statusText;

    // Preserve raw error data for detailed error reporting
    result.rawErrorData = errorData;

    // Check if it's a validation error with structured details
    if (errorData?.error?.details?.validationErrors) {
      result.isValidationError = true;
      result.generalError = errorMessage;
      // Don't overwrite with generic message when we have structured details
    } else if (errorMessage.includes('Validation error:') || response.status === 400) {
      result.isValidationError = true;
      result.fieldErrors = parseValidationErrors(errorMessage);
      
      // If no specific field errors were parsed, show general validation message
      if (Object.keys(result.fieldErrors).length === 0) {
        result.generalError = 'Please check your input and try again.';
      }
    } else {
      // Handle other types of errors
      result.generalError = getErrorMessage(errorMessage, response.status);
    }
  } catch {
    // If we can't parse the response, provide a generic message
    result.generalError = getErrorMessage('', response.status);
  }

  return result;
};

/**
 * Parse concatenated validation error messages into field-specific errors
 */
export const parseValidationErrors = (errorMessage: string): Record<string, string> => {
  const fieldErrors: Record<string, string> = {};

  // Organization validation errors
  if (errorMessage.includes('String must contain at least 2 character(s)')) {
    if (errorMessage.toLowerCase().includes('name')) {
      fieldErrors.name = 'Name must be at least 2 characters';
    }
  }

  if (errorMessage.includes('Code must be 2-10 characters, uppercase letters, numbers, and underscores only')) {
    fieldErrors.code = 'Code must be 2-10 characters using only uppercase letters, numbers, and underscores';
  }

  if (errorMessage.includes('Invalid enum value. Expected')) {
    if (errorMessage.includes('INSURANCE_COMPANY')) {
      fieldErrors.type = 'Please select a valid organisation type';
    } else if (errorMessage.includes('role')) {
      fieldErrors.role = 'Please select a valid role';
    }
  }

  if (errorMessage.includes('already exists')) {
    if (errorMessage.toLowerCase().includes('code')) {
      fieldErrors.code = 'This organisation code is already taken. Please choose a different one.';
    } else if (errorMessage.toLowerCase().includes('email')) {
      fieldErrors.email = 'This email address is already registered. Please use a different one.';
    }
  }

  // User validation errors
  if (errorMessage.includes('Invalid email')) {
    fieldErrors.email = 'Please enter a valid email address';
  }

  if (errorMessage.includes('Password must be at least')) {
    fieldErrors.password = 'Password must be at least 8 characters long';
  }

  if (errorMessage.includes('Name is required') || errorMessage.includes('name.*required')) {
    fieldErrors.name = 'Name is required';
  }

  if (errorMessage.includes('Email is required') || errorMessage.includes('email.*required')) {
    fieldErrors.email = 'Email is required';
  }

  // Phone number validation
  if (errorMessage.includes('Invalid phone number')) {
    fieldErrors.phone = 'Please enter a valid phone number';
  }

  // Address validation
  if (errorMessage.includes('Invalid postal code')) {
    fieldErrors.postalCode = 'Please enter a valid postal code';
  }

  return fieldErrors;
};

/**
 * Get user-friendly error messages based on error content and status code
 */
export const getErrorMessage = (errorMessage: string, statusCode?: number): string => {
  // Handle specific error types
  if (errorMessage.includes('Product code already exists')) {
    return 'This product code is already taken. Please choose a different one.';
  }

  if (errorMessage.includes('Organization with this code already exists')) {
    return 'This organisation code is already taken. Please choose a different one.';
  }

  if (errorMessage.includes('User with this email already exists')) {
    return 'This email address is already registered. Please use a different one.';
  }

  if (errorMessage.includes('Organization not found')) {
    return 'The requested organisation could not be found.';
  }

  if (errorMessage.includes('User not found')) {
    return 'The requested user could not be found.';
  }

  if (errorMessage.includes('Cannot delete organization with active users')) {
    return 'Cannot delete this organisation because it has active users. Please remove or transfer users first.';
  }

  if (errorMessage.includes('Cannot delete organization with active policies')) {
    return 'Cannot delete this organisation because it has active policies.';
  }

  // Handle by status code
  switch (statusCode) {
    case 400:
      return 'Invalid request. Please check your input and try again.';
    case 401:
      return 'You are not authorized to perform this action. Please log in and try again.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource could not be found.';
    case 409:
      return 'This action conflicts with existing data. Please check for duplicates.';
    case 422:
      return 'The provided data is invalid. Please check your input.';
    case 500:
      return 'An internal server error occurred. Please try again later.';
    case 503:
      return 'The service is temporarily unavailable. Please try again later.';
    default:
      return errorMessage || 'An unexpected error occurred. Please try again.';
  }
};

/**
 * Create an enhanced error object from a fetch response
 */
export class ApiError extends Error {
  public statusCode: number;
  public fieldErrors: Record<string, string>;
  public isValidationError: boolean;
  public rawErrorData?: unknown; // Store raw backend error data

  constructor(message: string, statusCode: number, fieldErrors: Record<string, string> = {}, isValidationError = false, rawErrorData?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.fieldErrors = fieldErrors;
    this.isValidationError = isValidationError;
    this.rawErrorData = rawErrorData;
  }

  static async fromResponse(response: Response): Promise<ApiError> {
    const parsedError = await parseApiError(response);
    
    return new ApiError(
      parsedError.generalError || 'API request failed',
      response.status,
      parsedError.fieldErrors,
      parsedError.isValidationError,
      parsedError.rawErrorData
    );
  }
}

/**
 * Helper to handle fetch responses and throw enhanced errors
 */
export const handleApiResponse = async (response: Response): Promise<unknown> => {
  if (!response.ok) {
    throw await ApiError.fromResponse(response);
  }

  const result = await response.json();
  return result;
}; 